import { AddTenantModal, DeleteConfirmModal, DynamicTable, IconImage, PageHeader, RestrictUserModal, UnblockUserModal } from "@/components";
import { search_img, company_placeholder } from "@/constants";
import { useDebounce, useSearchQuery, useSnackbar } from "@/hooks";
import { useEnableDisableTenantMutation, useGetAllTenantsQuery, useLazyGetAccessSignedUrlQuery, useDeleteTenantMutation } from "@/services";
import { InputAdornment } from "@mui/material";
import { FC, ReactElement, useState, useEffect } from "react";
import { IoMdAdd } from "react-icons/io";
import {
  HeaderRightNode,
  StyledContainedButton,
  StyledTextInput,
} from "./styles";
import { tenantColumns } from "@/columns/TDADMIN/tenants";
import { useGetTenantAllowedPagesQuery } from "@/graphql/page-access";


const defaultTenantDetails: ITenant = {
  _id: "",
  companyName: "",
  companyEmail: "",
  businessAddress: "",
  companyLogo: "",
  tenantType: "TRADEDIVE_CLIENT",
};

type ModalType = "delete" | "restrict" | "unblock" | "none";
  
interface ModalState {
  type: ModalType;
  open: boolean;
  tenantName: string;
  _id : string;
  email: string;
  isRestricted: boolean;
}

const initialModalState: ModalState = {
  type: "none",
  open: false,
  tenantName: "",
  _id: "",
  email: "",
  isRestricted: false
};

const TDADMINTenantManagement: FC = (): ReactElement => {
  const [modalState, setModalState] = useState<ModalState>(initialModalState);
  const { updateQuery, getQueryValue } = useSearchQuery();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showAddTenantModal, setShowAddTenantModal] = useState(false);
  const [tenantDetails, setTenantDetails] = useState<ITenant>(defaultTenantDetails);
  const [processedData, setProcessedData] = useState<ITenant[]>([]);
  const [getAccessSignedUrl, { isLoading:urlLoading }] = useLazyGetAccessSignedUrlQuery();


  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllTenantsQuery(
    {
      pageSize: limit,
      pageNo: page,
      searchKeyword: getQueryValue("search"),
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [deleteTenant, { isLoading: deleteTenantLoading }] = useDeleteTenantMutation();
  const [enableDisableTenant, { isLoading: enableDisableTenantLoading }] = useEnableDisableTenantMutation();
  const { show: toast } = useSnackbar();
  const { data = [], meta } = apiData || {};
  const { countDocs: totalCount = 0 } = meta || {};
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedForPages, setSelectedForPages] = useState<ITenant | null>(null);
  useGetTenantAllowedPagesQuery(
    { tenantId: selectedForPages?._id },
    { skip: !selectedForPages?._id }
  );

  // Process tenant data to get signed URLs for company logos
  useEffect(() => {
    const fetchSignedUrls = async () => {
      if (!data.length) {
        setProcessedData([]);
        return;
      }
      try {
        const processedTenants = await Promise.all(
          data.map(async (tenant) => {
            // If tenant has a company logo, get a signed URL for it
            if (tenant.companyLogo) {
              try {
                const { signedUrl } = await getAccessSignedUrl({
                  fileName: tenant.companyLogo,
                }).unwrap();
                
                return {
                  ...tenant,
                  companyLogo: signedUrl,
                };
              } catch (error) {
                console.error("Error fetching signed URL:", error);
                return {
                  ...tenant,
                  companyLogo: company_placeholder,
                };
              }
            }
            // If no company logo, use the default preview image
            return {
              ...tenant,
              companyLogo: company_placeholder,
            };
          })
        );
        setIsProcessing(false);
        setProcessedData(processedTenants);
      } catch (error) {
        setIsProcessing(false);
        console.error("Error processing tenants:", error);
        return data.map(tenant => ({
          ...tenant,
          companyLogo: company_placeholder
        }));
      }
    };

    fetchSignedUrls();
  }, [data, getAccessSignedUrl]);

  const debouncedSearch = useDebounce((val: string) => {
    setPage(1);
    updateQuery({ search: val });
  }, 500);

  const handleRowClick = (tenant: ITenant) => {
    setTenantDetails(tenant);
    setShowAddTenantModal(true);
    setSelectedForPages(tenant);
  };

  const handleOpenAddClientModal = () => {
    setShowAddTenantModal(true);
    setTenantDetails(defaultTenantDetails);
    setSelectedForPages(null);
  };

  const handleTriggerModal = (item: ITenant, type: string) => {
    setModalState({
      type: type as ModalType,
      open: true,
      tenantName: item.companyName || '',
      _id: item._id || '',
      email: item.companyEmail || '',
      isRestricted: item.isRestricted || false,
    });
  };

  const handleCloseModal = () => {
    setModalState(initialModalState);
  };

  const handleUpdateTenantStatus = async () => {
    try {
      await enableDisableTenant({
        tenantId: modalState._id,
        isRestricted: !modalState.isRestricted
      }).unwrap();
      handleCloseModal();
      setIsProcessing(true);
      toast("Tenant status updated successfully", "", "success"); 
    } catch (error: any) {
      console.error("Error updating tenant status:", error);
      toast(error?.message || "Failed to update tenant status", "", "error");
    }
  };

  const handleDeletePartner = async () => {
    try {
      await deleteTenant({
        tenantId: modalState._id
      }).unwrap();
      handleCloseModal();
      setIsProcessing(true);
      toast("Tenant deleted successfully", "", "success");
    } catch (error: any) {
      console.error("Error deleting tenant:", error);
      toast(error?.message || "Failed to delete tenant", "", "error");
    }
  };

  // Pages editing is now handled exclusively inside AddTenantModal



  return (
    <>
      <PageHeader
        title={`Tenant Management`}
        count={totalCount}
        rightNode={
          <HeaderRightNode gap={2}>
            <StyledTextInput
              type="text"
              placeholder="Search"
              handleChangeValue={debouncedSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconImage src={search_img} size="auto" />
                  </InputAdornment>
                ),
              }}
            />
            <StyledContainedButton
              startIcon={<IoMdAdd />}
              size="large"
              title="Add Tenant"
              onClick={handleOpenAddClientModal}
            />
          </HeaderRightNode>
        }
      />
      <DynamicTable
        data={processedData}
        columns={tenantColumns(handleTriggerModal)}
        isLoading={isProcessing || isLoading || isFetching || urlLoading }
        handlePageChange={setPage}
        handleLimitChange={(e) => {
          setPage(1);
          setLimit(e);
        }}
        isFullHeight
        limit={limit}
        page={page}
        total={totalCount}
        hidePagination={false}
        onRowClick={handleRowClick}
        stickyHeader
        hideScrollBars
        sx={{
          flex: 1,
          "& .MuiTable-root": {
            borderCollapse: "collapse",
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
        }}
      />
      {/* Pages editor removed from inline list view; it is now only inside AddTenantModal */}
      {/* Add Client Modal */}
      {showAddTenantModal && (
        <AddTenantModal
          show={showAddTenantModal}
          setShow={setShowAddTenantModal}
          selectedTenant={tenantDetails}
        />
      )}

      {/* Delete confirmation Modal */}
      {modalState.type === "delete" && (
        <DeleteConfirmModal
          open={modalState.open}
          itemName={`Tenant “${modalState.tenantName}”`}
          subText="tenant"
          onConfirm={handleDeletePartner}
          maxWidth="xs"
          onClose={handleCloseModal}
          btnLoader={deleteTenantLoading}
        />
      )}

      {modalState.type === "restrict" && (
        <UnblockUserModal
          open={modalState.open}
          name={modalState.tenantName}
          onConfirm={handleUpdateTenantStatus}
          onClose={handleCloseModal}
          loading={enableDisableTenantLoading}
        />
      )}

      {modalState.type === "unblock" && (
        <RestrictUserModal
          open={modalState.open}
          name={modalState.tenantName}
          onConfirm={handleUpdateTenantStatus}
          onClose={handleCloseModal}
          loading={enableDisableTenantLoading}
          description="tenant"
        />
      )}


    </>
  );
};

export default TDADMINTenantManagement;